// Portal › Kullanıcılar — "Form daveti gönder" modalı (M16 T20, E6/DR10): proje seçici + ayrı
// düğme (checkbox yan etkisi YOK). Hata tek genel mesaj; "zaten davetli" için kısa not.

import { useEffect, useId, useState } from "react";
import type { ClientAdminProject, ClientUser } from "@mahir/shared";
import { ApiError } from "../../api/client";
import { inviteToProject } from "../../api/client-admin";
import { CLIENT_ADMIN_COPY as C } from "../../lib/client-admin-copy";

interface Props {
  clientId: string;
  user: ClientUser | null;
  projects: ClientAdminProject[];
  onClose: () => void;
  onInvited: () => void;
}

export function ClientProjectInviteModal(props: Props): JSX.Element | null {
  const { clientId, user, projects, onClose, onInvited } = props;
  const [projectId, setProjectId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();
  const selectId = useId();

  useEffect(() => {
    if (!user) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape" && !loading) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [user, loading, onClose]);

  if (!user) return null;
  const invitedIds = new Set(user.projects.map((p) => p.projectId));
  const available = projects.filter((p) => !invitedIds.has(p.id));

  async function handleSubmit(): Promise<void> {
    if (!user || !projectId) return;
    setLoading(true);
    setError(null);
    try {
      await inviteToProject(clientId, projectId, { email: user.email, fullName: user.fullName });
      setProjectId("");
      onInvited();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "already_participant"
          ? `${C.projectInvite.error} (${C.projectInvite.already})`
          : C.projectInvite.error,
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: "rgba(0,0,0,0.4)" }}
      onClick={() => !loading && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="rounded-xl p-6 max-w-md w-full mx-4"
        style={{ background: "var(--color-surface)", color: "var(--color-text)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id={titleId}
          className="mb-1"
          style={{ fontFamily: "var(--font-display)", fontSize: "1.25rem" }}
        >
          {C.projectInvite.title}
        </h2>
        <p className="mb-1 text-sm" style={{ color: "var(--color-text-2)" }}>
          {C.projectInvite.description}
        </p>
        <p className="mb-4 text-sm" style={{ color: "var(--color-text)", fontWeight: 600 }}>
          {user.fullName} · <span style={{ fontWeight: 400 }}>{user.email}</span>
        </p>

        <label
          htmlFor={selectId}
          className="block text-xs mb-1"
          style={{ color: "var(--color-text-2)" }}
        >
          {C.projectInvite.project}
        </label>
        {available.length === 0 ? (
          <div className="text-sm mb-3" style={{ color: "var(--color-text-3)" }}>
            {C.drawer.noProjects}
          </div>
        ) : (
          <select
            id={selectId}
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            disabled={loading}
            className="w-full p-2 rounded border text-sm mb-3"
            style={{ borderColor: "var(--color-border)", fontFamily: "var(--font-sans)" }}
          >
            <option value="">{C.projectInvite.choose}</option>
            {available.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-md p-3 mb-3 text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
            }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text-2)",
            }}
          >
            {C.inviteModal.cancel}
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!projectId || loading}
            className="px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontWeight: 600,
            }}
          >
            {loading ? C.projectInvite.submitting : C.projectInvite.submit}
          </button>
        </div>
      </div>
    </div>
  );
}
